import { type Inquiry } from "@/lib/api";
import { formatDate } from "@/lib/date";
import { getServiceBadge, getStatusBadge, maskName } from "@/lib/constants";

const PAGE_SIZE = 10;

function truncate(text: string, max: number): string {
  if (!text) return "";
  return text.length > max ? text.slice(0, max) + "..." : text;
}

interface InquiryListProps {
  inquiries: Inquiry[];
  loading: boolean;
  error: string;
  page: number;
  totalCount: number;
  expandedId: number | null;
  onToggleExpand: (id: number) => void;
  onPageChange: (page: number) => void;
  onRetry: () => void;
  onShowForm: () => void;
}

export default function InquiryList({
  inquiries,
  loading,
  error,
  page,
  totalCount,
  expandedId,
  onToggleExpand,
  onPageChange,
  onRetry,
  onShowForm,
}: InquiryListProps) {
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 w-8 h-8 flex items-center justify-center"></i>
        <span className="text-foreground-500 text-sm">문의 목록을 불러오는 중...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <div className="inline-flex flex-col items-center gap-3 p-8 rounded-lg bg-accent-50 border border-accent-200">
          <i className="ri-error-warning-line text-3xl text-accent-600 w-8 h-8 flex items-center justify-center"></i>
          <p className="text-accent-800 text-sm">{error}</p>
          <button
            onClick={onRetry}
            className="px-5 py-2 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  if (inquiries.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="inline-flex flex-col items-center gap-3 p-8 rounded-lg bg-background-100 border border-background-200">
          <i className="ri-chat-1-line text-3xl text-foreground-400 w-8 h-8 flex items-center justify-center"></i>
          <p className="text-foreground-600 text-sm">아직 등록된 문의가 없습니다.</p>
          <button
            onClick={onShowForm}
            className="px-5 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors"
          >
            첫 문의 남기기
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full rounded-lg border border-background-200/70 overflow-hidden bg-background-50">
        <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3.5 bg-background-100/60 border-b border-background-200/70 text-xs font-medium text-foreground-500 tracking-wide uppercase">
          <div className="col-span-1 text-center">No.</div>
          <div className="col-span-2">서비스</div>
          <div className="col-span-4">문의 내용</div>
          <div className="col-span-2 text-center">작성자</div>
          <div className="col-span-1 text-center">상태</div>
          <div className="col-span-2 text-right">날짜</div>
        </div>

        {inquiries.map((inq, idx) => {
          const isExpanded = expandedId === inq.id;
          const serviceBadgeClass = getServiceBadge(inq.service);
          const statusBadgeClass = getStatusBadge(inq.status);

          return (
            <div key={inq.id}>
              <div
                onClick={() => onToggleExpand(inq.id)}
                className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-background-100 hover:bg-background-50/60 cursor-pointer transition-colors items-center"
              >
                <div className="col-span-1 text-center text-xs text-foreground-400 font-mono">
                  {totalCount - ((page - 1) * PAGE_SIZE + idx)}
                </div>
                <div className="col-span-3 md:col-span-2">
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${serviceBadgeClass}`}>
                    {inq.service}
                  </span>
                </div>
                <div className="col-span-8 md:col-span-4 text-sm text-foreground-800 truncate">
                  {truncate(inq.message, 50) || "(내용 없음)"}
                </div>
                <div className="hidden md:block md:col-span-2 text-center text-sm text-foreground-500 whitespace-nowrap">
                  {maskName(inq.name)}
                </div>
                <div className="hidden md:flex md:col-span-1 justify-center items-center">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${statusBadgeClass}`}>
                    {inq.status || "접수"}
                  </span>
                </div>
                <div className="hidden md:block md:col-span-2 text-right text-xs text-foreground-400 whitespace-nowrap">
                  {formatDate(inq.created_at)}
                </div>
              </div>

              {isExpanded && (
                <div className="px-5 py-5 bg-background-100/40 border-b border-background-200/40">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="flex gap-2">
                      <span className="text-foreground-400 w-16 flex-shrink-0">이름</span>
                      <span className="text-foreground-900 font-medium">{maskName(inq.name)}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-foreground-400 w-16 flex-shrink-0">회사</span>
                      <span className="text-foreground-900">{inq.company || "-"}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-foreground-400 w-16 flex-shrink-0">서비스</span>
                      <span className="text-foreground-900">{inq.service}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-foreground-400 w-16 flex-shrink-0">스크린</span>
                      <span className="text-foreground-900">{inq.screen || "-"}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-foreground-400 w-16 flex-shrink-0">현장</span>
                      <span className="text-foreground-900">{inq.site || "-"}</span>
                    </div>
                    <div className="flex gap-2 items-center">
                      <span className="text-foreground-400 w-16 flex-shrink-0">상태</span>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass}`}>
                        {inq.status || "접수"}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-foreground-400 w-16 flex-shrink-0">접수일</span>
                      <span className="text-foreground-900">{formatDate(inq.created_at)}</span>
                    </div>
                  </div>
                  <div className="mt-4 p-4 rounded-md bg-background-50 border border-background-200/60">
                    <p className="text-sm text-foreground-800 whitespace-pre-wrap leading-relaxed">
                      {inq.message || "(내용 없음)"}
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <button
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page === 1}
            className="w-9 h-9 rounded-md border border-background-200 bg-background-50 text-foreground-700 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center"></i>
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`w-9 h-9 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                p === page
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-200 bg-background-50 text-foreground-700 hover:bg-background-100"
              }`}
            >
              {p}
            </button>
          ))}

          <button
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="w-9 h-9 rounded-md border border-background-200 bg-background-50 text-foreground-700 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center cursor-pointer transition-colors"
          >
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
          </button>
        </div>
      )}

      <div className="text-center mt-10">
        <a
          href="/#contact"
          className="inline-flex items-center gap-2 px-8 py-3.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-edit-line w-4 h-4 flex items-center justify-center"></i>
          문의하기
        </a>
      </div>
    </>
  );
}